import React, {
  FunctionComponent,
  useState,
  useEffect,
  ChangeEvent,
} from "react";
import { Button, ChoiceList, Dialog, TextField } from "@cmsgov/ds-medicare-gov";
import { County } from "../@types";
import * as api from "../api";
import { useTranslate, sendTealiumEvent } from "../helpers";
import {
  limitInputLength,
  TealiumEventProps,
} from "../helpers/objectUtilities";
import states from "../pages/EnrollmentFormPage/states";
import thinX from "../assets/thin_x.svg";

interface StatePickerProps {
  label: string;
  onStateSelect: (county?: County, zipcode?: string) => void;
  initialZip?: string;
  initialFips?: string;
  className?: string;
  onInputTealiumProps?: TealiumEventProps;
  onBtnClickTealiumProps?: TealiumEventProps;
  pickerIndex?: number;
}

export const StatePicker: FunctionComponent<StatePickerProps> = ({
  label,
  onStateSelect,
  initialZip,
  initialFips,
  className,
  onInputTealiumProps,
  onBtnClickTealiumProps,
  pickerIndex = 0,
}) => {
  const t = useTranslate();
  const [fetching, setFetching] = useState(false);
  const [zipcode, setZipcode] = useState("");
  const [zipEntered, setZipEntered] = useState(false);
  const [countyResults, setCountyResults] = useState<County[]>([]);
  const [selectedCounty, setSelectedCounty] = useState<County | null>(null);

  const [zipErrorMessage, setZipErrorMessage] = useState<undefined | string>(
    undefined
  );
  const [selectStateError, setSelectStateError] = useState<undefined | string>(
    undefined
  );

  const [showModal, setShowModal] = useState(false);
  let autocompleteInput: HTMLInputElement;

  const getSetOfStatesFromCounties = (countyList: County[]): Set<string> =>
    new Set(countyList.map(county => county.state));

  const searchForCounties = async (autofillZip?: string): Promise<void> => {
    setZipErrorMessage(undefined);

    let input;
    if (autofillZip) {
      input = autofillZip;
      autocompleteInput.value = input;
    } else {
      input = autocompleteInput.value;
    }

    if (onBtnClickTealiumProps) {
      sendTealiumEvent(
        onBtnClickTealiumProps.event_action,
        onBtnClickTealiumProps.event_label
      );
    }

    if (/^\d{5}$/.test(input)) {
      setZipcode(input);
      setFetching(true);
      setCountyResults([]);
      onStateSelect();
      if (!zipEntered) {
        // Not recording zip, and only sending once
        if (onInputTealiumProps) {
          sendTealiumEvent(
            onInputTealiumProps.event_action,
            onInputTealiumProps.event_label
          );
        }
        setZipEntered(true);
      }
      try {
        const counties = await api.getCounties(input);
        setCountyResults(counties);

        if (autofillZip) {
          const countyMatch = counties.find(
            county => county.fips === initialFips
          );

          if (countyMatch) {
            onStateSelect(countyMatch, autofillZip);
          }
        }

        const setOfCounties = getSetOfStatesFromCounties(counties);

        if (setOfCounties.size === 1) {
          onStateSelect(counties[0], input);
        } else if (setOfCounties.size > 1) {
          setShowModal(true);
        }
      } catch (error) {
        setZipErrorMessage(t("state_picker.error_fetching_message"));
      }
      setFetching(false);
    } else {
      setZipErrorMessage(t("state_picker.error_message"));
    }
  };

  const onStateSelected = (e: React.ChangeEvent<HTMLInputElement>): void => {
    sendTealiumEvent("Medigap - Find a Policy", "CTA: Location Selected");
    const selected = JSON.parse(e.target.value);

    setSelectedCounty(selected);

    if (!showModal) {
      onStateSelect(selected, zipcode);
    }
  };

  const onSubmit = (e: ChangeEvent<HTMLFormElement>): void => {
    e.preventDefault();
    searchForCounties();
  };

  useEffect(() => {
    if (initialZip) {
      searchForCounties(initialZip);
    }
  }, []);

  return (
    <div className={`StatePicker ${className ? className : ""}`}>
      <form
        className="StatePicker__input e2e-select-county"
        onSubmit={onSubmit}
      >
        <TextField
          label={label}
          labelClassName="questionHeading e2e-enter-zip StatePicker__label"
          className="ds-c-field--statePicker"
          onChange={(): void =>
            limitInputLength("statePicker input", 5, pickerIndex)
          }
          name="zip"
          inputRef={(ref: HTMLInputElement): HTMLInputElement =>
            (autocompleteInput = ref)
          }
          errorMessage={zipErrorMessage}
        />

        <Button
          className="e2eStatePickerSelectBtn"
          type="submit"
          variation="primary"
        >
          {t("state_picker.button_text")}
        </Button>
        {fetching}
      </form>
      {fetching && <p>{t("app.loading")}...</p>}
      {showModal && (
        <Dialog
          heading={t("county_picker.choose_your_state")}
          className="SelectStateModal"
          closeText={
            <img aria-hidden="true" src={thinX} role="presentation" alt=" " />
          }
          closeButtonSize="big"
          closeButtonVariation="transparent"
          headerClassName="SelectStateModal__header"
          escapeExitDisabled={true}
          underlayClickExits={false}
          onExit={(): void => setShowModal(false)}
          actions={[
            <Button
              className="ds-u-margin-right--2"
              key="primary"
              variation="primary"
              onClick={(): void => {
                if (selectedCounty) {
                  onStateSelect(selectedCounty, zipcode);
                  sendTealiumEvent("Medigap - Find a Policy", "CTA: Continue");
                } else {
                  setSelectStateError(
                    t("state_picker.modal.error_message.state_not_selected")
                  );
                }
              }}
            >
              {t("state_picker.modal.button_text")}
            </Button>,
          ]}
        >
          <ChoiceList
            name="state"
            label={""}
            type="radio"
            errorMessage={selectStateError}
            choices={Array.from(getSetOfStatesFromCounties(countyResults)).map(
              state => ({
                label: states.find(s => s.abbreviation === state)?.name || "",
                value: JSON.stringify(
                  countyResults.find(county => county.state === state)
                ),
              })
            )}
            onChange={onStateSelected}
          />
        </Dialog>
      )}
    </div>
  );
};

export default StatePicker;
